import { useState } from "react";
import { Link } from "react-router-dom";
import { Edit } from "lucide-react";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import TrashIcon from "../../Icons/TrashIcon";
import DeleteModal from "../../Shared/DeleteModal";
import { AdminCBQExamListItem } from "../../../api/Quiz/GetAdminCBQExams";
import { DeleteCBQExam } from "../../../api/Quiz/DeleteCBQExam";

interface CbqExamRowProps {
  exam: AdminCBQExamListItem;
  courseId: string;
}

const statusStyle = (status?: string) => {
  switch ((status || "").toLowerCase()) {
    case "published":
      return "bg-green-100 text-green-700";
    case "archived":
      return "bg-gray-200 text-gray-600";
    case "ready":
    case "active":
      return "bg-blue-100 text-blue-700";
    default:
      return "bg-yellow-100 text-yellow-700";
  }
};

const formatDate = (dateString?: string | null) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  const hours = date.getHours() % 12 || 12;
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = date.getHours() >= 12 ? "PM" : "AM";

  return `${day}.${month}.${year} - ${hours}:${minutes} ${ampm}`;
};

const CbqExamRow = ({ exam, courseId }: CbqExamRowProps) => {
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const queryClient = useQueryClient();

  const title = exam.title || "Untitled exam";
  const scenarios = exam.scenariosCount ?? exam._count?.scenarios ?? null;

  const handleDelete = async (id: string) => {
    const result = await DeleteCBQExam(id);
    if (result.error) {
      toast.error(result.error.message || "Failed to delete CBQ exam");
      return;
    }
    toast.success("CBQ exam deleted successfully");
    queryClient.invalidateQueries({ queryKey: ["admin-cbq-exams"] });
  };

  return (
    <>
      <tr>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          <div className="inline-flex rounded-full text-left py-0.5 text-sm font-semibold">
            {title}
          </div>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${statusStyle(
              exam.status
            )}`}
          >
            {exam.status || "created"}
          </span>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          <div className="inline-flex rounded-full text-left py-0.5 text-sm font-semibold">
            {exam.mode === "PROD_WITH_MCQ" ? "With MCQ" : "Standalone"}
          </div>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          <div className="inline-flex rounded-full text-left py-0.5 text-sm font-semibold">
            {scenarios ?? "—"}
          </div>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          <div className="inline-flex rounded-full text-left py-0.5 text-sm font-semibold">
            {formatDate(exam.createdAt)}
          </div>
        </td>
        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
          <div className="flex justify-center items-stretch gap-3">
            <div className="flex flex-shrink-0 rounded-xl justify-center items-center border p-3 text-sm font-semibold cursor-pointer hover:bg-gray-50">
              <Link
                to={`/dashboard/courses/${courseId}/cbq-exams/${exam.id}`}
                title="Edit exam"
              >
                <Edit size={18} />
              </Link>
            </div>
            <div className="flex flex-shrink-0 rounded-xl justify-center items-center border p-3 text-sm font-semibold cursor-pointer hover:bg-gray-50">
              <button onClick={() => setIsDeleteOpen(true)} title="Delete exam">
                <TrashIcon />
              </button>
            </div>
          </div>
        </td>
      </tr>

      <DeleteModal
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        mutationFn={handleDelete}
        itemId={exam.id}
        queryKey="admin-cbq-exams"
        itemName={title}
        message="Its scenarios, questions, and any student attempts will be removed. This cannot be undone."
      />
    </>
  );
};

export default CbqExamRow;
